import classNames from 'classnames';
import React from 'react';
import { ResizableBox, ResizableProps } from 'react-resizable';
import { DialogCssProperties } from './Dialog';
import { DEFAULT_MIN_SIZES, DIALOG_MARGIN } from './DialogConstant';

export interface IDialogResizableBoxProps {
  children: React.ReactNode;
  windowWidth: number;
  windowHeight: number;
  width: number;
  height: number;
  styles?: DialogCssProperties;
  onResize: ResizableProps['onResize'];
  onResizeStart: ResizableProps['onResizeStart'];
  onResizeStop: ResizableProps['onResizeStop'];
  fullscreen?: boolean;
  responsive?: boolean;
  isResizeDisabled?: boolean;
  dialogMargin: number;
}

/**
 * Resizable box for our dialog.  Wraps contents in a resizable box, but also handles differences for fullscreen / responsive.
 *
 * We can't easily conditionally render children in differing JSX trees, without un-mounting and re-mounting
 * the children [because of how react works](https://leoasis.github.io/posts/2017/06/26/reparenting-a-component/)
 *
 * This can lead to data loss, so having a resizer that doesn't do anything is the lesser evil.
 *
 * Copyright (c) 2021 Decisions All Rights Reserved
 */
export function DialogResizableBox({
  children,
  fullscreen,
  windowHeight,
  windowWidth,
  styles,
  responsive,
  isResizeDisabled,
  dialogMargin,
  ...resizableProps
}: IDialogResizableBoxProps) {
  const margin = dialogMargin ?? DIALOG_MARGIN;
  if (responsive) {
    return <>{children}</>;
  }
  const resizeProps = fullscreen
    ? {
        className: classNames('dp-dialog-resizable', 'dp-dialog-resizable--hidden'),
        width: windowWidth,
        height: windowHeight,
      }
    : {
        ...resizableProps,
        className: classNames('dp-dialog-resizable', { 'dp-dialog-resizable--hidden': isResizeDisabled }),
        maxConstraints: [
          Math.max(styles?.minWidth ?? 0 + margin * 2, styles?.maxWidth ?? windowWidth - margin * 2),
          Math.max(styles?.minHeight ?? 0 + margin * 2, styles?.maxHeight ?? windowHeight - margin * 2),
        ] as [number, number],
        minConstraints: [styles?.minWidth || DEFAULT_MIN_SIZES, styles?.minHeight || DEFAULT_MIN_SIZES] as [
          number,
          number
        ],
      };

  return <ResizableBox {...resizeProps}>{children}</ResizableBox>;
}
